import { useIsSupported, useOnline } from '@react-hooks-library/core';
import dayjs from 'dayjs';
import { CloseCircle, Danger } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import BrowserList from '@/components/home/browser-list';
import InfoTable, { type InfoData } from '@/components/home/info-table';
import useClientId from '@/hooks/pwa/use-client-id';
import useUserPlatform, { useUserHints } from '@/hooks/use-user-platform';
import ROUTES from '@/routes';
import { useConfigurationStore } from '@/stores/configuration';
import { setCompatibilityChecked } from '@/stores/device';
import { APP_VERSION_HASH } from '@/utils/constants';
import { getHost } from '@/utils/parse-url';

export default function CompatibilityIssues() {
  const { t } = useTranslation();
  const { isDesktop, isStandalone } = useUserPlatform();
  const { browser } = useUserHints();
  const { clientId } = useClientId();
  const { endpoint } = useConfigurationStore();
  const isOnline = useOnline();
  const isWebsocketSupported = useIsSupported(() => !!window.WebSocket);
  const isIndexedDBSupported = useIsSupported(() => !!window.indexedDB);
  const isWorkerSupported = useIsSupported(() => !!window.Worker);

  const data: InfoData[] = [
    {
      label: 'compatibility.version',
      key: 'version',
      value: `${APP_VERSION_HASH} ${isDesktop ? 'D' : 'M'}/${isStandalone ? 'PWA' : 'WEB'}`,
    },
    { label: 'compatibility.client-id', key: 'clientId', value: clientId },
    { label: 'compatibility.app-endpoint', key: 'app', value: getHost(window.location.origin) },
    { label: 'compatibility.api-endpoint', key: 'api', value: getHost(endpoint.API_ENDPOINT) },
    { label: 'compatibility.file-endpoint', key: 'file', value: getHost(endpoint.FILE_ENDPOINT) },
    { label: 'compatibility.file-upload-endpoint', key: 'file-upload', value: getHost(endpoint.FILEUPLOAD_ENDPOINT) },
    { label: 'compatibility.ws-endpoint', key: 'ws', value: getHost(endpoint.WEBSOCKET_ENDPOINT) },
    {
      label: 'compatibility.browser',
      key: 'browser',
      value: browser,
    },
    {
      label: 'compatibility.user-agent',
      key: 'ua',
      value: navigator.userAgent,
    },
  ];
  return (
    <div className='flex flex-col gap-6 flex-center w-full h-full bg-neutral-950/50 p-6'>
      <div className='flex rounded-xl flex-1 overflow-y-auto p-6 bg-neutral-0 flex-col items-stretch shadow'>
        <h1 className='flex gap-4 items-center text-h2 desktop:text-h3 mb-4 text-metal-700'>
          <Danger
            variant='Outline'
            className='text-red-600 size-8'
          />

          <span>{t('compatibility.title')}</span>
        </h1>

        <p className='text-metal-700 text-body1'>{t('compatibility.description')}</p>

        <BrowserList />

        <h3 className='text-metal-700 text-body1 mb-1'>{t('compatibility.current-browser-info')}</h3>

        <InfoTable
          className='grid grid-cols-[auto_1fr] auto-rows-auto mb-2'
          data={data}
        />

        <InfoTable
          className='grid auto-cols-auto [&>dd]:border-b-0 [&>dd]:border-e [&>dt]:border-b-0 grid-flow-col auto-rows-auto mb-2'
          data={[
            { label: 'compatibility.websocket', key: 'ws', value: isWebsocketSupported },
            { label: 'compatibility.indexed-db', key: 'idb', value: isIndexedDBSupported },
            { label: 'compatibility.worker', key: 'worker', value: isWorkerSupported },
          ]}
        />

        <InfoTable
          className='grid grid-cols-[auto_1fr] auto-rows-auto mb-2'
          data={[
            { label: 'compatibility.network', key: 'network', value: isOnline },
            { label: 'compatibility.now-time', key: 'now-time', value: dayjs().format() },
          ]}
        />
      </div>

      <Link
        className='btn btn-circle btn-ghost p-0'
        onPointerDown={() => setCompatibilityChecked()}
        to={ROUTES.MESSAGES.$path()}>
        <CloseCircle
          variant='Bold'
          className='text-neutral-0 size-10'
        />
      </Link>
    </div>
  );
}
